import { useState, useEffect, useRef } from 'react';
import {
  XMarkIcon,
  PaperClipIcon,
  DocumentIcon,
  PhotoIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { createTask, generateFutureWeeks, uploadTaskAttachments } from '../api/monday-meeting.api';
import type { User, TaskPriority, MondayMeetingTask, MondayMeetingCategory } from '../types/monday-meeting.types';
import './TaskModal.css';

interface TaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  users: User[];
  categories: MondayMeetingCategory[];
  currentWeek: { year: number; week: number } | null;
  editTask?: MondayMeetingTask;
}

const PRIORITY_OPTIONS: { value: TaskPriority; label: string; color: string }[] = [
  { value: 'low', label: 'Basse', color: '#6b7280' },
  { value: 'medium', label: 'Moyenne', color: '#f59e0b' },
  { value: 'high', label: 'Haute', color: '#ef4444' },
];

export function TaskModal({
  isOpen,
  onClose,
  onSuccess,
  users,
  categories = [],
  currentWeek,
  editTask,
}: TaskModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('medium');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedWeek, setSelectedWeek] = useState<{ year: number; week: number } | null>(null);
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);

  // Attachment state
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const futureWeeks = generateFutureWeeks(12);

  useEffect(() => {
    if (isOpen) {
      if (editTask) {
        setTitle(editTask.title);
        setDescription(editTask.description || '');
        setPriority(editTask.priority);
        setSelectedCategory(editTask.category_id || '');
        setSelectedWeek({ year: editTask.due_year, week: editTask.due_week });
        setSelectedAssignees(editTask.assignees.map((a) => a.user_id));
      } else {
        setTitle('');
        setDescription('');
        setPriority('medium');
        setSelectedCategory('');
        setSelectedWeek(null);
        setSelectedAssignees([]);
      }
      setPendingFiles([]);
      setError(null);
      setIsClosing(false);
    }
  }, [isOpen, editTask, currentWeek]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 250);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!title.trim()) {
      setError('Le titre est obligatoire');
      return;
    }

    if (!selectedWeek) {
      setError('La semaine est obligatoire');
      return;
    }

    setLoading(true);

    try {
      // Create the task
      const response = await createTask({
        title: title.trim(),
        description: description.trim() || undefined,
        priority,
        due_year: selectedWeek.year,
        due_week: selectedWeek.week,
        category_id: selectedCategory || undefined,
        assignee_ids: selectedAssignees,
      });

      // Upload pending files if any
      if (pendingFiles.length > 0 && response.data?.id) {
        setUploadingFiles(true);
        try {
          await uploadTaskAttachments(response.data.id, pendingFiles, (percent) => {
            setUploadProgress(percent);
          });
        } catch (uploadErr) {
          console.error('Error uploading attachments:', uploadErr);
          // Task was created, attachments failed - still close but log error
        } finally {
          setUploadingFiles(false);
          setUploadProgress(0);
        }
      }

      onSuccess();
    } catch (err: any) {
      console.error('Error creating task:', err);
      setError(err.response?.data?.message || 'Erreur lors de la création de la tâche');
    } finally {
      setLoading(false);
    }
  };

  // File handling functions
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('File input changed:', e.target.files);
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      console.log('Adding files:', newFiles);
      setPendingFiles(prev => {
        const updated = [...prev, ...newFiles];
        console.log('Updated pending files:', updated);
        return updated;
      });
      e.target.value = ''; // Reset input
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      setPendingFiles(prev => [...prev, ...Array.from(e.dataTransfer.files)]);
    }
  };

  const removePendingFile = (index: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return PhotoIcon;
    return DocumentIcon;
  };

  const toggleAssignee = (userId: string) => {
    setSelectedAssignees((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const getCurrentPriority = () =>
    PRIORITY_OPTIONS.find((p) => p.value === priority) || PRIORITY_OPTIONS[1];

  if (!isOpen) return null;

  return (
    <div className={`tm-overlay ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
      <div
        className={`tm-panel ${isClosing ? 'closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="tm-header">
          <h2>{editTask ? 'Modifier la tâche' : 'Nouvelle tâche'}</h2>
          <button className="tm-close-btn" onClick={handleClose}>
            <XMarkIcon />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="tm-content">
          {error && <div className="tm-error">{error}</div>}

          {/* Title */}
          <div className="tm-field">
            <label htmlFor="title">Titre *</label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Préparer le rapport mensuel"
              autoFocus
            />
          </div>

          {/* Description */}
          <div className="tm-field">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Détails supplémentaires (optionnel)"
              rows={3}
            />
          </div>

          {/* Sujet & Priority & Due Week Row */}
          <div className="tm-row tm-row-three">
            {/* Sujet */}
            <div className="tm-field tm-field-third">
              <label htmlFor="category">Sujet</label>
              <select
                id="category"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="">Aucun</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Priority */}
            <div className="tm-field tm-field-third">
              <label htmlFor="priority">Priorité</label>
              <div className="tm-select-wrapper">
                <span
                  className="tm-select-dot"
                  style={{ background: getCurrentPriority().color }}
                />
                <select
                  id="priority"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as TaskPriority)}
                >
                  {PRIORITY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Due Week */}
            <div className="tm-field tm-field-third">
              <label htmlFor="dueWeek">Échéance *</label>
              <select
                id="dueWeek"
                value={selectedWeek ? `${selectedWeek.year}-${selectedWeek.week}` : ''}
                onChange={(e) => {
                  if (e.target.value) {
                    const [year, week] = e.target.value.split('-').map(Number);
                    setSelectedWeek({ year, week });
                  } else {
                    setSelectedWeek(null);
                  }
                }}
                className={!selectedWeek ? 'placeholder' : ''}
              >
                <option value="">Sélectionner...</option>
                {futureWeeks.map((w) => (
                  <option key={`${w.year}-${w.week}`} value={`${w.year}-${w.week}`}>
                    {w.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Assignees */}
          <div className="tm-field">
            <label>Assignés</label>
            <div className="tm-assignees">
              {users.map((user) => (
                <label
                  key={user.id}
                  className={`tm-assignee-option ${selectedAssignees.includes(user.id) ? 'selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedAssignees.includes(user.id)}
                    onChange={() => toggleAssignee(user.id)}
                  />
                  <div className="tm-assignee-avatar">
                    {user.name.split(' ').map((n) => n[0]).join('').substring(0, 2)}
                  </div>
                  <div className="tm-assignee-info">
                    <span className="tm-assignee-name">{user.name}</span>
                    <span className="tm-assignee-role">{user.role}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Attachments */}
          <div className="tm-field">
            <label>Pièces jointes</label>

            {/* Pending Files List */}
            {pendingFiles.length > 0 && (
              <div className="tm-pending-files">
                {pendingFiles.map((file, index) => {
                  const FileIcon = getFileIcon(file.type);
                  return (
                    <div key={`${file.name}-${index}`} className="tm-pending-file">
                      <FileIcon className="tm-pending-file-icon" />
                      <div className="tm-pending-file-info">
                        <span className="tm-pending-file-name">{file.name}</span>
                        <span className="tm-pending-file-size">{formatFileSize(file.size)}</span>
                      </div>
                      <button
                        type="button"
                        className="tm-pending-file-remove"
                        onClick={() => removePendingFile(index)}
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Dropzone */}
            <div
              className={`tm-dropzone ${isDragging ? 'dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => {
                console.log('Dropzone clicked, fileInputRef:', fileInputRef.current);
                fileInputRef.current?.click();
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
              />
              <PaperClipIcon className="tm-dropzone-icon" />
              <span className="tm-dropzone-text">
                Glissez des fichiers ou cliquez pour parcourir
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="tm-actions">
            <button type="button" className="tm-cancel-btn" onClick={handleClose} disabled={loading || uploadingFiles}>
              Annuler
            </button>
            <button type="submit" className="tm-submit-btn" disabled={loading || uploadingFiles}>
              {uploadingFiles
                ? `Téléchargement... ${uploadProgress}%`
                : loading
                  ? 'Création...'
                  : editTask
                    ? 'Modifier'
                    : pendingFiles.length > 0
                      ? `Créer la tâche (${pendingFiles.length} fichier${pendingFiles.length > 1 ? 's' : ''})`
                      : 'Créer la tâche'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TaskModal;
